import { Action } from "@opstel/query/v2/models/action";
import { ActionType } from "@opstel/query/v2/models/actionType";
import { TrackCode } from "@opstel/query/models/track";
import { DefinitionList, type Definition } from "@shared/EDS/components/DefinitionList";
import { LockButton } from "./LockButton";
import { ChainDescription } from "./ChainDescription/ChainDescription";
import { DATETIME_FORMAT_NO_DATE, DURATION_FORMAT } from "@shared/common/utils/luxonHelpers";
import { ShuntingUnit } from "@opstel/query/models/shuntingUnit";
import { useConfig } from "@shared/common/contexts/config/useConfig";
import { Config } from "@shared/envVariables";
import { LoadingState } from "@shared/common/components/State/LoadingState";
import { useActionQuery } from "../../SBDAction/useActionQuery";
import { ErrorState } from "@shared/common/components/State/ErrorState";

interface DetailsDataProviderProps {
  readonly actionId: string;
  readonly shuntingUnit: ShuntingUnit;
  readonly onEdit: () => void;
}

export function DetailsDataProvider({ actionId, shuntingUnit, onEdit }: DetailsDataProviderProps) {
  const { data: action, isPending, isError } = useActionQuery({ actionId });

  if (isPending) return <LoadingState fullPage={false} />;
  if (isError) return <ErrorState />;

  return <Details action={action} shuntingUnit={shuntingUnit} onEdit={onEdit} />;
}

interface DetailsProps {
  readonly action: Action;
  readonly shuntingUnit: ShuntingUnit;
  readonly onEdit: () => void;
}

export function Details({ action, shuntingUnit, onEdit }: DetailsProps) {
  const config = useConfig();
  const shouldShowEditButton =
    action.changeability !== "Fixed" &&
    action.type === "Move" &&
    config.featureFlags.isActionMutationEnabled;

  return (
    <>
      {/* Replace with tractie-section-header when released */}
      <div className="flex h-8 items-center" slot="start">
        <h3 className="m-0">{getHeader(action)}</h3>
      </div>

      <div className="flex flex-col gap-m">
        <tractie-divider className="-mx-(--_container-padding-inline-start)" />
        <DefinitionList contents={getDescriptionListContent(action, shuntingUnit, config)} />
        <div className="-mt-(--tractie-spacing-gap-s) flex h-8 items-center gap-s">
          {shouldShowEditButton && (
            <tractie-button
              data-testid="sbd-sidepanel-details-edit-button"
              level="tertiary"
              icon-only
              onClick={onEdit}
              aria-label="Beweging wijzigen"
            >
              <tractie-icon name="edit" />
            </tractie-button>
          )}
          <LockButton actionId={action.id} changeability={action.changeability} />
        </div>
        <tractie-divider className="-mx-(--_container-padding-inline-start)" />
        <ChainDescription shuntingUnit={shuntingUnit} />
      </div>
    </>
  );
}

function getHeader(action: Action): string {
  const actionTypeName = ActionType.getDutchTranslation(action.type);
  return action.type === "Move" && action.movementCode
    ? `${actionTypeName} ${action.movementCode}`
    : actionTypeName;
}

function getDescriptionListContent(
  action: Action,
  shuntingUnit: ShuntingUnit,
  config: Config,
): Definition[] {
  const definitionListContent: Definition[] = [];
  if (Action.isMoveOrTeleport(action)) {
    if (!action.isNetworkMovement || !TrackCode.isForeignTrack(action.stablingInfraStart)) {
      definitionListContent.push({
        term: "Vertrek",
        description: `${action.startTime.toFormat(DATETIME_FORMAT_NO_DATE)} ${action.stablingInfraStart}`,
      });
    }
    if (!action.isNetworkMovement || !TrackCode.isForeignTrack(action.stablingInfraEnd)) {
      definitionListContent.push({
        term: "Aankomst",
        description: `${action.endTime.toFormat(DATETIME_FORMAT_NO_DATE)} ${action.stablingInfraEnd}`,
      });
    }
    if (!action.isNetworkMovement) {
      definitionListContent.push({
        term: "Duur",
        description: action.endTime.diff(action.startTime).rescale().toFormat(DURATION_FORMAT),
      });
    }
  }
  if (Action.isSingleTrackAction(action)) {
    definitionListContent.push(
      {
        term: "Starttijd",
        description: `${action.startTime.toFormat(DATETIME_FORMAT_NO_DATE)} ${action.stablingInfra}`,
      },
      {
        term: "Eindtijd",
        description: `${action.endTime.toFormat(DATETIME_FORMAT_NO_DATE)} ${action.stablingInfra}`,
      },
      {
        term: "Duur",
        description: action.endTime.diff(action.startTime).rescale().toFormat(DURATION_FORMAT),
      },
    );
  }

  if (config.debug.showInfo) {
    addDebugInfo(definitionListContent, action, shuntingUnit);
  }
  return definitionListContent;
}

function addDebugInfo(
  definitionListContent: Definition[],
  action: Action,
  shuntingUnit: ShuntingUnit,
) {
  definitionListContent.push({
    term: "Lengte",
    description: `${ShuntingUnit.totalLength(shuntingUnit).inMeters()} m`,
  });

  if (Action.isSingleTrackAction(action)) {
    definitionListContent.push({
      term: "Afstand tot A",
      description: `${Math.round(action.distanceToSource.inMeters())} m`,
    });
  } else {
    definitionListContent.push(
      {
        term: "V afstand tot A",
        description: `${Math.round(action.distanceToSourceStart.inMeters())} m`,
      },
      {
        term: "A afstand tot A",
        description: `${Math.round(action.distanceToSourceEnd.inMeters())} m`,
      },
    );
  }
}
